import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Play, ShieldCheck, Stethoscope, Cpu, BadgeDollarSign, Building2,
} from 'lucide-react';
import { BRAND_NAME } from '../utils/brand';
import { trackCtaClick } from '../utils/analytics';
import Reveal from './Reveal';
import Marquee from './Marquee';

//
// Hero — matches client mockup "Web Hero 16x9.png":
//   • Dark teal/navy gradient background with heart + Ai watermarks
//     (image asset: /Web Section 1 - Hero Background.png)
//   • Centred eyebrow + 2-line headline (first word "Smarter" in teal)
//   • Sub-line + dual CTA + disclaimer
//   • Two clickable product cards (Weight Loss / Lab Tests) overlapping
//     the bottom edge — images from /Sub Category - *.png
//   • Four-item trust strip at the bottom
//
// Motion: Reveal stagger (left-slide) for the centre column; the cards
// fade-up on entry. Respects prefers-reduced-motion globally.
//

const TRUST_ITEMS = [
  { icon: ShieldCheck,     label: 'Licensed Partner Clinics' },
  { icon: Stethoscope,     label: 'Doctor-Led Guidance' },
  { icon: Cpu,             label: 'AI-Assisted Assessment' },
  { icon: BadgeDollarSign, label: 'Transparent Programme Fees' },
  { icon: Building2,       label: 'Clinic-Supported Care' },
];

function PrimaryCta() {
  return (
    <Link
      to="/intake"
      onClick={() => trackCtaClick({ page: 'home_hero', button_label: 'Am I Qualified?', destination: '/intake' })}
      className="cta-sheen cta-pulse-glow inline-flex items-center gap-1.5 sm:gap-2 font-bold uppercase tracking-wide rounded-full whitespace-nowrap
                 px-4 sm:px-6 py-2.5 sm:py-3.5 text-[11px] sm:text-sm
                 bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-card"
    >
      Am I Qualified? <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function SecondaryCta() {
  return (
    <Link
      to="/how-it-works"
      onClick={() => trackCtaClick({ page: 'home_hero', button_label: 'See How It Works', destination: '/how-it-works' })}
      className="hidden sm:inline-flex items-center gap-1.5 sm:gap-2 font-medium rounded-full whitespace-nowrap
                 px-3.5 sm:px-6 py-2.5 sm:py-3.5 text-[11px] sm:text-sm
                 border border-white/30 text-white hover:bg-white/10 transition-colors backdrop-blur"
    >
      <Play size={12} className="fill-current" /> See How It Works
    </Link>
  );
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  // Instant jump — no smooth scroll through the trust marquee so the user
  // lands cleanly on the target section (client PDF: "jump precisely to
  // Page 2 without seeing carousel").
  const navH = 64; // sticky navbar height in px (h-16)
  const y = el.getBoundingClientRect().top + window.scrollY - navH;
  window.scrollTo({ top: y, left: 0, behavior: 'auto' });
}

function ProductCard({ to, scrollTo, image, label, badge }) {
  const destination = scrollTo ? `#${scrollTo}` : to;
  const cardClasses =
    'group relative block overflow-hidden rounded-2xl border border-white/15 ' +
    'bg-white shadow-card hover:shadow-glow transition-shadow text-left w-full';
  const inner = (
    <>
      {/* object-contain — whole image visible, no crop, no zoom.
          Short landscape aspect + soft brand-tinted bg blends any
          leftover letterbox with the card so gaps aren't obvious. */}
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-50/40">
        <img
          src={image}
          alt={label}
          className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-105"
          draggable="false"
        />
      </div>
      <div className="flex items-center justify-between bg-white px-2.5 sm:px-5 py-1.5 sm:py-2.5">
        <div className="min-w-0">
          <span className="block text-ink-900 font-bold text-[11px] leading-tight sm:text-base">{label}</span>
          {badge && (
            <span className="block text-[8px] sm:text-[10px] font-semibold uppercase tracking-wider text-brand-600">
              {badge}
            </span>
          )}
        </div>
        <span className="inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full
                         text-brand-600 border border-brand-100
                         group-hover:bg-brand-600 group-hover:text-white transition-colors shrink-0">
          <ArrowRight size={12} className="sm:hidden" />
          <ArrowRight size={16} className="hidden sm:block" />
        </span>
      </div>
    </>
  );
  if (scrollTo) {
    return (
      <button
        type="button"
        onClick={() => {
          trackCtaClick({ page: 'home_hero', button_label: label, destination });
          scrollToSection(scrollTo);
        }}
        className={cardClasses}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={to}
      onClick={() => trackCtaClick({ page: 'home_hero', button_label: label, destination })}
      className={cardClasses}
    >
      {inner}
    </Link>
  );
}

export default function Hero() {
  return (
    <>
    <section className="relative isolate overflow-hidden bg-ink-900 text-white flex-1 flex flex-col justify-start sm:justify-center">
      {/* Background image — Web Section 1 - Hero Background.png */}
      <img
        src="/Web Section 1 - Hero Background.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 -z-20 w-full h-full object-cover"
      />
      {/* Tone overlay to deepen the brand-correct contrast */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(180deg, rgba(2,33,63,0.30) 0%, rgba(2,33,63,0.55) 70%, rgba(2,33,63,0.80) 100%)',
        }}
      />

      <div className="container-x pt-[2.5svh] sm:pt-16 lg:pt-24 pb-[2svh] sm:pb-12 lg:pb-16
                      flex-1 flex flex-col justify-evenly sm:block sm:flex-none">
        {/* ── Centred copy ───────────────────────────────────────────── */}
        <div className="text-center max-w-3xl mx-auto">
          <Reveal direction="left" delay={240}>
            <h1 className="font-bold tracking-tight leading-[1.12] sm:leading-[0.98]
                           text-[clamp(1.75rem,7.5svh,2.75rem)] sm:text-5xl md:text-6xl lg:text-7xl">
              <span className="text-white">A clearer path to</span>
              <br />
              <span className="text-white">doctor-guided</span>
              <br />
              <span className="text-brand-400">medical weight loss.</span>
            </h1>
          </Reveal>

          <Reveal direction="left" delay={400}>
            <p className="mt-[2svh] sm:mt-6 text-[clamp(0.95rem,2.6svh,1.25rem)] sm:text-xl md:text-2xl text-white/85 max-w-2xl mx-auto leading-snug">
              Answer a few questions to explore a personalized pathway with doctor guidance.
            </p>
          </Reveal>

          <Reveal direction="left" delay={540}>
            <div className="mt-[2.5svh] sm:mt-7 flex flex-nowrap items-center justify-center gap-2 sm:gap-3">
              <PrimaryCta />
              <SecondaryCta />
            </div>
          </Reveal>

          <Reveal direction="left" delay={680}>
            <p className="mt-[1.2svh] sm:mt-4 text-[clamp(0.65rem,1.4svh,0.75rem)] sm:text-xs text-white/55 text-center leading-snug">
              Programme suitability is subject to a doctor's consultation.
              <br />
              Individual results may vary.
            </p>
          </Reveal>
        </div>

        {/* ── Two product cards ──────────────────────────────────────── */}
        {/* Wider container = less empty gap on left/right of the cards.
            Stays centered so the copy above still aligns. */}
        <div className="mt-[2.5svh] sm:mt-8 grid grid-cols-2 gap-3 sm:gap-5 max-w-md sm:max-w-3xl mx-auto">
          <Reveal delay={150}>
            <ProductCard
              scrollTo="section-glp"
              image="/01 Weight Management Plan Checklist.png"
              label="Weight Loss Programme"
            />
          </Reveal>
          <Reveal delay={300}>
            <ProductCard
              to="/preventive-health"
              image="/Sub Category - Lab Test.png"
              label="Lab Tests"
              badge="Coming Soon"
            />
          </Reveal>
        </div>
      </div>

      {/* SR-only brand title for accessibility */}
      <h2 className="sr-only">{BRAND_NAME}</h2>
    </section>

    {/* ── 5-item trust strip — continuous marquee below the dark hero,
        teal icons + Intelligence-Navy labels, pauses on hover. ─── */}
    <Marquee
      bg="bg-white"
      speedSec={20}
      desktopSpeedSec={30}
      items={TRUST_ITEMS.map(({ icon: Icon, label }) => (
        <span key={label} className="inline-flex items-center gap-2.5">
          <Icon size={20} className="text-brand-600 shrink-0" strokeWidth={1.75} />
          <span className="text-[11px] sm:text-sm font-semibold text-ink-900">
            {label}
          </span>
        </span>
      ))}
    />
    </>
  );
}
